import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, X } from 'lucide-react';
import { EMAIL_REASONS, EMAIL_STATUSES } from '@fenix/shared';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/StatusBadge';
import { DataTablePagination } from '@/components/DataTablePagination';
import { EmptyState } from '@/components/EmptyState';
import { emailsApi } from '@/lib/api/endpoints';
import {
  EMAIL_REASON_LABEL,
  EMAIL_STATUS_LABEL,
  EMAIL_STATUS_VARIANT,
  fmtDateTime,
} from '@/lib/format';

const ALL = '__all__';

/** Todos os e-mails da segunda via, com o status que os webhooks do Resend trouxeram. */
export function EmailsPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const query = {
    page: Number(params.get('page') ?? 1),
    pageSize: 25,
    status: params.get('status') ?? undefined,
    reason: params.get('reason') ?? undefined,
    search: params.get('search') ?? undefined,
  };
  const hasFilters = [...params.keys()].some((k) => k !== 'page');
  const set = (k: string, v: string | number | undefined) => {
    const next = new URLSearchParams(params);
    if (v === undefined || v === '' || v === ALL) next.delete(k);
    else next.set(k, String(v));
    if (k !== 'page') next.delete('page');
    setParams(next, { replace: true });
  };
  const q = useQuery({ queryKey: ['emails', query], queryFn: () => emailsApi.list(query) });

  const journey = (m: {
    deliveredAt: string | null;
    openedAt: string | null;
    clickedAt: string | null;
  }) =>
    [
      m.deliveredAt ? `entregue ${fmtDateTime(m.deliveredAt)}` : null,
      m.openedAt ? `aberto ${fmtDateTime(m.openedAt)}` : null,
      m.clickedAt ? `clicado ${fmtDateTime(m.clickedAt)}` : null,
    ]
      .filter(Boolean)
      .join(' · ');

  return (
    <div>
      <PageHeader
        title="E-mails"
        description="Segunda via do link de checkout: enviados quando o SMS falha, combinados na ligação ou pelo painel"
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Input
          placeholder="E-mail ou nome do lead"
          className="w-64"
          defaultValue={query.search ?? ''}
          onKeyDown={(e) => {
            if (e.key === 'Enter') set('search', (e.target as HTMLInputElement).value.trim());
          }}
        />
        <Select value={query.status ?? ALL} onValueChange={(v) => set('status', v)}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Todos os status</SelectItem>
            {EMAIL_STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {EMAIL_STATUS_LABEL[s] ?? s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={query.reason ?? ALL} onValueChange={(v) => set('reason', v)}>
          <SelectTrigger className="w-52">
            <SelectValue placeholder="Motivo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Todos os motivos</SelectItem>
            {EMAIL_REASONS.map((r) => (
              <SelectItem key={r} value={r}>
                {EMAIL_REASON_LABEL[r] ?? r}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {hasFilters && (
          <Button size="sm" variant="ghost" onClick={() => setParams({}, { replace: true })}>
            <X /> Limpar
          </Button>
        )}
        {q.data && (
          <span className="ml-auto text-sm text-muted-foreground">{q.data.total} e-mail(s)</span>
        )}
      </div>

      <div className="rounded-lg border bg-card">
        {q.isLoading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-10" />
            ))}
          </div>
        ) : q.data?.items.length ? (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Enviado</TableHead>
                  <TableHead>Para</TableHead>
                  <TableHead>Lead</TableHead>
                  <TableHead>Produto</TableHead>
                  <TableHead>Motivo</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Percurso</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {q.data.items.map((m) => (
                  <TableRow
                    key={m.id}
                    className={m.callId ? 'cursor-pointer' : undefined}
                    onClick={() => m.callId && navigate(`/chamadas/${m.callId}`)}
                  >
                    <TableCell className="whitespace-nowrap text-sm">
                      {fmtDateTime(m.sentAt)}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{m.toEmail}</TableCell>
                    <TableCell>{m.lead.name ?? '—'}</TableCell>
                    <TableCell className="text-sm">
                      {m.productName ?? m.lead.productCodename ?? '—'}
                      {m.brandName && (
                        <span className="block text-xs text-muted-foreground">{m.brandName}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {EMAIL_REASON_LABEL[m.reason] ?? m.reason}
                    </TableCell>
                    <TableCell>
                      <StatusBadge
                        label={EMAIL_STATUS_LABEL[m.status] ?? m.status}
                        className={
                          EMAIL_STATUS_VARIANT[m.status] ?? 'bg-secondary text-secondary-foreground'
                        }
                      />
                      {m.errorMessage && (
                        <span
                          className="block max-w-xs truncate text-xs text-destructive"
                          title={m.errorMessage}
                        >
                          {m.errorMessage}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {journey(m) || '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <DataTablePagination
              page={q.data.page}
              pageSize={q.data.pageSize}
              total={q.data.total}
              onPageChange={(p) => set('page', p)}
            />
          </>
        ) : (
          <div className="p-6">
            <EmptyState
              icon={Mail}
              title={hasFilters ? 'Nenhum e-mail com esses filtros' : 'Nenhum e-mail enviado'}
              description={
                hasFilters
                  ? 'Limpe os filtros para ver todos.'
                  : 'O e-mail sai quando o SMS não é entregue, quando a IA combina e-mail na ligação ou pelo botão na chamada. Configure o Resend em Configurações → E-mail.'
              }
            />
          </div>
        )}
      </div>
    </div>
  );
}
